import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import MDDatePicker from 'components/MDDatePicker';
import MDTimePicker from 'components/MDTimePicker';
import { api } from 'api';
import { usePermissions } from 'hooks/usePermissions';

const MAX_BABIES = 3;
const emptyBaby = () => ({ weight: '', sex: '', date_of_delivery: '', time_of_delivery: '', place_of_delivery: '' });

// Helper function to format date to dd/mm/yyyy
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '-';
  }
};

const FinalOutcomeModal = ({ open, onClose, procedureId, primaryColor = '#00798E' }) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('v1-embryo-outcome-create');
  const canEdit = hasPermission('v1-embryo-outcome-update');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalOutcome, setFinalOutcome] = useState(null);
  const [formData, setFormData] = useState({
    remarks: '',
    delivery_gestation: '',
    delivery_no: '', // "No of Issues" — also controls how many baby rows show (max 3)
    delivery_instruction: '',
    cong_abnormality: '',
  });
  const [babies, setBabies] = useState([]);

  // Load final outcome data
  const loadFinalOutcome = async () => {
    try {
      setLoading(true);
      const response = await api.embryoOutcome.getEmbryoOutcomesByProcedure(procedureId);
      if (response && response.data && response.data.length > 0) {
        const outcomeData = response.data[0];
        if (outcomeData.finalOutcome) {
          setFinalOutcome(outcomeData.finalOutcome);
          const noOfIssues = outcomeData.finalOutcome.delivery_no ?? '';
          setFormData({
            remarks: outcomeData.finalOutcome.remarks || '',
            delivery_gestation: outcomeData.finalOutcome.delivery_gestation || '',
            delivery_no: noOfIssues,
            delivery_instruction: outcomeData.finalOutcome.delivery_instruction || '',
            cong_abnormality: outcomeData.finalOutcome.cong_abnormality || '',
          });

          let loadedBabies = (outcomeData.finalOutcome.babies || []).map(b => ({
            weight: b.weight ?? '',
            sex: b.sex || '',
            date_of_delivery: b.date_of_delivery || '',
            time_of_delivery: b.time_of_delivery || '',
            place_of_delivery: b.place_of_delivery || '',
          }));
          // Backward compatibility: older records saved before the per-baby
          // rows existed only have the single flat delivery_* fields — show
          // those as "Baby 1" if no per-baby rows were saved.
          if (loadedBabies.length === 0 && (
            outcomeData.finalOutcome.delivery_weight ||
            outcomeData.finalOutcome.delivery_sex ||
            outcomeData.finalOutcome.date_of_delivery ||
            outcomeData.finalOutcome.place_of_delivery
          )) {
            loadedBabies = [{
              weight: outcomeData.finalOutcome.delivery_weight ?? '',
              sex: outcomeData.finalOutcome.delivery_sex || '',
              date_of_delivery: outcomeData.finalOutcome.date_of_delivery || '',
              time_of_delivery: '',
              place_of_delivery: outcomeData.finalOutcome.place_of_delivery || '',
            }];
          }
          const count = noOfIssues !== '' ? parseInt(noOfIssues, 10) : loadedBabies.length;
          while (loadedBabies.length < count) loadedBabies.push(emptyBaby());
          setBabies(loadedBabies.slice(0, MAX_BABIES));
        }
      }
    } catch (error) {
      console.error('Error loading final outcome:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to load final outcome',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && procedureId) {
      loadFinalOutcome();
    }
  }, [open, procedureId]);

  // Handle form field change
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // "No of Issues" drives how many baby rows are shown (max 3)
  const handleNoOfIssuesChange = (value) => {
    setFormData(prev => ({ ...prev, delivery_no: value }));
    const count = value ? parseInt(value, 10) : 0;
    setBabies(prev => {
      const next = [...prev];
      while (next.length < count) next.push(emptyBaby());
      return next.slice(0, count);
    });
  };

  const handleBabyChange = (index, field, value) => {
    setBabies(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
  };

  // Handle save
  const handleSave = async () => {
    try {
      setSaving(true);

      // Check if at least one field is filled
      const hasFormData = Object.values(formData).some(val => val !== '' && val !== null);
      const hasBabyData = babies.some(b => Object.values(b).some(val => val !== '' && val !== null));
      if (!hasFormData && !hasBabyData) {
        Swal.fire({
          icon: 'warning',
          title: 'No Data',
          text: 'Please fill at least one field',
        });
        return;
      }

      const payload = {
        irm_procedures_id: procedureId,
        finalOutcome: {
          remarks: formData.remarks || null,
          delivery_gestation: formData.delivery_gestation || null,
          delivery_no: formData.delivery_no !== '' ? parseInt(formData.delivery_no, 10) : null,
          delivery_instruction: formData.delivery_instruction || null,
          cong_abnormality: formData.cong_abnormality || null,
          babies: babies.slice(0, formData.delivery_no !== '' ? parseInt(formData.delivery_no, 10) : babies.length).map(b => ({
            weight: b.weight ? parseFloat(b.weight) : null,
            sex: b.sex || null,
            date_of_delivery: b.date_of_delivery || null,
            time_of_delivery: b.time_of_delivery || null,
            place_of_delivery: b.place_of_delivery || null,
          })),
        },
      };

      // First check if there's an existing outcome record
      const response = await api.embryoOutcome.getEmbryoOutcomesByProcedure(procedureId);
      
      if (response && response.data && response.data.length > 0) {
        // Update existing outcome with final outcome
        const outcomeId = response.data[0].outcome.id;
        await api.embryoOutcome.updateEmbryoOutcome(outcomeId, payload);
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Final outcome updated successfully',
          timer: 1500,
        });
      } else {
        // Create new outcome with only final outcome data
        await api.embryoOutcome.createEmbryoOutcome(payload);
        Swal.fire({
          icon: 'success',
          title: 'Saved',
          text: 'Final outcome saved successfully',
          timer: 1500,
        });
      }

      loadFinalOutcome();
    } catch (error) {
      console.error('Error saving final outcome:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save final outcome',
      });
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const handleClose = () => {
    setFormData({
      remarks: '',
      delivery_gestation: '',
      delivery_no: '',
      delivery_instruction: '',
      cong_abnormality: '',
    });
    setBabies([]);
    setFinalOutcome(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold" sx={{ color: primaryColor }}>
            Final Outcome (Delivery Details)
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!canCreate && !canEdit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You don&apos;t have permission to edit final outcome
              </Alert>
            )}

            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Delivery Gestation"
                      value={formData.delivery_gestation}
                      onChange={(e) => handleChange('delivery_gestation', e.target.value)}
                      disabled={!canCreate && !canEdit}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="No of Issues"
                      value={formData.delivery_no}
                      onChange={(e) => handleNoOfIssuesChange(e.target.value)}
                      disabled={!canCreate && !canEdit}
                      sx={{
                        '& .MuiOutlinedInput-root': { minHeight: '40px' },
                        '& .MuiSelect-select': { minHeight: '40px !important', boxSizing: 'border-box', display: 'flex', alignItems: 'center' },
                      }}
                    >
                      <MenuItem value="">—</MenuItem>
                      {Array.from({ length: MAX_BABIES + 1 }, (_, i) => i).map(n => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {babies.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                    Delivery Details
                  </Typography>
                  {babies.map((baby, index) => (
                    <Box key={index}>
                      {index > 0 && <Divider sx={{ my: 2 }} />}
                      <Typography variant="body2" fontWeight="bold" mb={1} sx={{ color: primaryColor }}>
                        Baby {index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4} md={2}>
                          <TextField
                            fullWidth
                            label="Weight"
                            type="number"
                            value={baby.weight}
                            onChange={(e) => handleBabyChange(index, 'weight', e.target.value)}
                            disabled={!canCreate && !canEdit}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                          <TextField
                            fullWidth
                            label="Sex"
                            value={baby.sex}
                            onChange={(e) => handleBabyChange(index, 'sex', e.target.value)}
                            disabled={!canCreate && !canEdit}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <MDDatePicker
                            value={baby.date_of_delivery}
                            onChange={(date) => handleBabyChange(index, 'date_of_delivery', date)}
                            label="Date"
                            fullWidth
                            disabled={!canCreate && !canEdit}
                          />
                        </Grid>
                        <Grid item xs={6} sm={6} md={2}>
                          <MDTimePicker
                            value={baby.time_of_delivery}
                            onChange={(time) => handleBabyChange(index, 'time_of_delivery', time || '')}
                            label="Time"
                            fullWidth
                            disabled={!canCreate && !canEdit}
                          />
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                          <TextField
                            fullWidth
                            label="Place"
                            value={baby.place_of_delivery}
                            onChange={(e) => handleBabyChange(index, 'place_of_delivery', e.target.value)}
                            disabled={!canCreate && !canEdit}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Congenital Abnormality"
                      multiline
                      rows={2}
                      value={formData.cong_abnormality}
                      onChange={(e) => handleChange('cong_abnormality', e.target.value)}
                      disabled={!canCreate && !canEdit}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Instruction"
                      multiline
                      rows={2}
                      value={formData.delivery_instruction}
                      onChange={(e) => handleChange('delivery_instruction', e.target.value)}
                      disabled={!canCreate && !canEdit}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Remarks"
                      multiline
                      rows={2}
                      value={formData.remarks}
                      onChange={(e) => handleChange('remarks', e.target.value)}
                      disabled={!canCreate && !canEdit}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ 
                  color: primaryColor, 
                  borderColor: primaryColor, 
                  '&:hover': { 
                    backgroundColor: primaryColor, 
                    color: 'white !important', 
                    borderColor: primaryColor, 
                    opacity: 0.9 
                  } 
                }}
              >
                Cancel
              </Button>
              {(canCreate || canEdit) && (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ 
                    backgroundColor: primaryColor, 
                    color: 'white !important', 
                    '&:hover': { 
                      backgroundColor: primaryColor, 
                      opacity: 0.9 
                    } 
                  }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Save'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

FinalOutcomeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procedureId: PropTypes.number.isRequired,
  primaryColor: PropTypes.string,
};

export default FinalOutcomeModal;
